import React, { createContext, useState, useMemo, ReactNode, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';

type ThemeContextType = {
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
};

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'dark',
  toggleColorMode: () => {},
});

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme_mode');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme_mode', mode);
  }, [mode]);

  const toggleColorMode = () => setMode((p) => (p === 'light' ? 'dark' : 'light'));

  const theme = useMemo(() => {
    const isDark = mode === 'dark';
    return createTheme({
      palette: {
        mode,
        primary: { main: '#7C6FFF', light: '#9D93FF', dark: '#5A4FD4' },
        secondary: { main: '#FF6B8A' },
        success: { main: '#4ADE80' },
        warning: { main: '#FBBF24' },
        error: { main: '#F87171' },
        info: { main: '#38BDF8' },
        background: {
          default: isDark ? '#080A10' : '#F4F6FB',
          paper: isDark ? '#10131A' : '#FFFFFF',
        },
        divider: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
        text: {
          primary: isDark ? '#E8EAF0' : '#1A1D26',
          secondary: isDark ? '#8B90A0' : '#6B7080',
        },
      },
      shape: { borderRadius: 14 },
      typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        h4: { fontWeight: 800, letterSpacing: '-0.02em' },
        h5: { fontWeight: 700, letterSpacing: '-0.01em' },
        h6: { fontWeight: 600 },
        body2: { lineHeight: 1.6 },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundImage: isDark
                ? 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(124,111,255,0.07) 0%, transparent 60%)'
                : 'none',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: { borderRadius: 12, padding: '10px 22px', fontSize: '0.875rem' },
            contained: {
              boxShadow: 'none',
              background: 'linear-gradient(135deg, #7C6FFF 0%, #9D7BFF 100%)',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(124,111,255,0.35)',
                background: 'linear-gradient(135deg, #6B5EEE 0%, #8B6AEE 100%)',
              },
            },
            outlined: {
              borderWidth: 1.5,
              '&:hover': { borderWidth: 1.5 },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              ...(isDark && {
                backgroundColor: 'rgba(16, 19, 26, 0.7)',
                backdropFilter: 'blur(20px)',
              }),
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                borderRadius: 12,
                '& fieldset': {
                  borderWidth: 1.5,
                  borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
                },
                '&:hover fieldset': {
                  borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: 2,
                },
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { fontWeight: 500, fontSize: '0.75rem' },
            outlined: {
              borderWidth: 1.5,
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: { borderRadius: 10, height: 5 },
            bar: { borderRadius: 10 },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              borderRadius: 8,
              fontSize: '0.75rem',
              fontWeight: 500,
              padding: '6px 12px',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: 20,
              ...(isDark && {
                backgroundColor: '#141820',
                border: '1px solid rgba(255,255,255,0.06)',
              }),
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              borderRadius: 14,
              ...(isDark && {
                backgroundColor: '#141820',
                border: '1px solid rgba(255,255,255,0.06)',
              }),
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.06)',
            },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
